import * as React from 'react';
import { sourceSystemApi } from '../services/sourceSystemApi';
import type { SourceSystemPanelState, SourceType } from '../types/source-system';

const ACTIVE_STATUSES = new Set(['pending', 'running']);
const POLL_INTERVAL_MS = 5_000;

/**
 * Returns a cheap string fingerprint of the fields that change during a sync run.
 * Used to skip re-renders when a poll response is identical to the current state.
 */
function statusFingerprint(data: SourceSystemPanelState): string {
  return JSON.stringify({
    ar: data.activeRun ? `${data.activeRun.jobRunUuid}:${data.activeRun.status}` : null,
    lr: data.lastRun
      ? `${data.lastRun.jobRunUuid}:${data.lastRun.status}:${data.lastRun.completedAt}`
      : null,
    rs: data.resources.map(
      (r) => `${r.resourceUuid}:${r.lastRunStatus}:${r.lastRunAt}:${r.recordsFetched}`
    ),
  });
}

/**
 * Fetches and optionally polls the panel state for a given source type.
 * Polling is enabled whenever there is an active (pending/running) sync.
 */
export function useSourceSystem(projectUuid: string, sourceType: SourceType) {
  const [state, setState] = React.useState<SourceSystemPanelState | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const lastFingerprintRef = React.useRef<string | null>(null);

  const hasActiveRun = React.useMemo(() => {
    if (!state) return false;
    return state.resources.some(
      (r) => r.lastRunStatus !== null && ACTIVE_STATUSES.has(r.lastRunStatus)
    );
  }, [state]);

  const load = React.useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true);
      setError(null);
      try {
        const data = await sourceSystemApi.getPanelState(projectUuid, sourceType);
        const fp = statusFingerprint(data);
        if (quiet && fp === lastFingerprintRef.current) return;
        lastFingerprintRef.current = fp;
        setState(data);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [projectUuid, sourceType]
  );

  // Initial load
  React.useEffect(() => {
    void load();
  }, [load]);

  // Poll while active run
  React.useEffect(() => {
    if (hasActiveRun) {
      intervalRef.current = setInterval(() => void load(true), POLL_INTERVAL_MS);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [hasActiveRun, load]);

  return { state, loading, error, refresh: load, hasActiveRun };
}
